'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import SafetyAndFooter from '@/components/SafetyAndFooter';
import AppNav from '@/components/AppNav';
import { createClient } from '@/lib/supabase/client';

interface FAQItem {
  q: string;
  a: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

const faqSections: FAQSection[] = [
  {
    title: 'Getting Started',
    items: [
      {
        q: 'What is Jamuhuri Singles Network?',
        a: 'Jamuhuri Singles Network, or JSN, is a values-based matchmaking and singles network created for Kenyan singles living in the United States. JSN is designed to help members discover, like, match, and connect with other eligible members in a safer and more intentional environment.',
      },
      {
        q: 'Who can join JSN?',
        a: 'JSN is currently intended for Kenyan singles living in the United States. During sign-up, members must use a valid U.S. phone number and complete account verification steps before accessing member features.',
      },
      {
        q: 'How do I create a JSN account?',
        a: 'To create an account, go to the Create Account page, complete the required profile fields, provide a valid U.S. phone number, agree to the required terms and SMS verification consent, upload a clear profile photo, and complete the verification steps shown during registration.',
      },
      {
        q: 'Why does JSN have several verification steps?',
        a: 'JSN uses verification steps to reduce fake accounts, scam profiles, and incomplete registrations. New members may need to verify email, verify phone number, upload a photo, and wait for photo approval before accessing member areas.',
      },
      {
        q: "Why can't I access Discover immediately after signing up?",
        a: 'New accounts must complete verification and photo review before full member access is granted. If your photo is still pending review, you will not be able to browse member profiles until it is approved.',
      },
    ],
  },
  {
    title: 'Email and Phone Verification',
    items: [
      {
        q: 'Why do I need to verify my email?',
        a: 'Email verification helps confirm that you own the email address used to create your account. It also helps protect your account and supports password recovery.',
      },
      {
        q: 'Why do I need to verify my phone number?',
        a: 'JSN currently serves Kenyan singles living in the United States. Requiring a valid U.S. phone number helps reduce fake accounts, scam profiles, and non-U.S. registrations. Your phone number is used for account verification and security.',
      },
      {
        q: 'Can I use a non-U.S. phone number?',
        a: 'No. JSN currently accepts U.S.-based members only, so sign-up requires a valid +1 U.S. phone number.',
      },
      {
        q: 'Can I use a Kenyan phone number?',
        a: 'Not for this version of JSN. This app is currently for Kenyan singles living in the United States. A separate future app or platform may serve members living in Kenya.',
      },
      {
        q: 'Can other members see my phone number?',
        a: 'No. Your phone number is used for verification and account security. It should not be shown publicly to other members.',
      },
      {
        q: 'I did not receive my SMS verification code. What should I do?',
        a: 'Check that your phone number is correct and that it is a valid U.S. number. You can try the Resend Code option if available. SMS delivery may sometimes be delayed by your mobile carrier or the messaging provider.',
      },
      {
        q: 'I did not receive my email verification message. What should I do?',
        a: 'Check your spam, junk, promotions, or updates folder. Make sure the email address you entered is correct. If the verification message does not arrive, try signing in again or restarting the registration process if prompted.',
      },
      {
        q: 'Can I resend the verification code?',
        a: 'Yes. If the Resend Code option is available, use it to request a new verification code. The newest code should be used.',
      },
    ],
  },
  {
    title: 'Photo Approval',
    items: [
      {
        q: 'Why does JSN review profile photos?',
        a: 'Photo review helps keep JSN safer and more authentic. It helps reduce fake profiles, unclear photos, inappropriate images, and profiles that do not appear to represent a real member.',
      },
      {
        q: 'What kind of photo should I upload?',
        a: 'Upload a clear, recent photo of yourself. Your face should be visible. Avoid group photos, blurry images, heavily filtered photos, inappropriate images, celebrity photos, stock images, or images that do not clearly show you.',
      },
      {
        q: 'How long does photo approval take?',
        a: 'Photo approval time may vary. During early launch, approvals may depend on admin availability. If your photo is pending, you will see a pending review notice.',
      },
      {
        q: 'Why was my photo rejected?',
        a: 'A photo may be rejected if it is unclear, does not show your face, appears inappropriate, looks fake or stock-like, includes someone else as the main subject, or does not meet JSN profile standards.',
      },
      {
        q: 'Can I upload a replacement photo?',
        a: 'Yes. If your photo is rejected, you should be able to upload a replacement photo for review. Upload a clear and recent photo of yourself.',
      },
      {
        q: "Why can't I browse profiles while my photo is pending?",
        a: 'JSN requires photo approval before member browsing to help keep the community authentic and safer for everyone.',
      },
    ],
  },
  {
    title: 'Free vs Premium',
    items: [
      {
        q: 'What can I do with a free approved account?',
        a: 'Free approved users can browse basic Discover profile cards and use Like or Pass. Free access is designed to let you explore the platform before upgrading.',
      },
      {
        q: 'What features require Premium?',
        a: 'Premium unlocks full profile viewing, Matches, messaging, premium sorting, Block and Report tools, and other member interaction features.',
      },
      {
        q: "Why can I Like or Pass but not message?",
        a: 'Messaging is a Premium feature. Free users can like or pass profiles, but must upgrade to Premium to read or send messages.',
      },
      {
        q: "Why can't I view full profiles?",
        a: 'Full profile viewing is a Premium feature. Free users can see basic profile cards, but Premium is required to view full details.',
      },
      {
        q: "Why can't I see Matches?",
        a: 'Matches and liked-member visibility are Premium features. Upgrade to Premium to view matches and access more interaction tools.',
      },
      {
        q: 'What are the JSN Premium plans?',
        a: 'JSN currently offers four Premium access plans: Flex for 1 month, Value for 3 months, Best for 6 months, and Elite for 12 months.',
      },
      {
        q: 'What happens when my Premium access expires?',
        a: 'When Premium expires, your account should return to free access unless you renew or upgrade again. You may still be able to browse basic profile cards and Like or Pass, but Premium features will be locked.',
      },
    ],
  },
  {
    title: 'Payments and Premium Access',
    items: [
      {
        q: 'How do I upgrade to Premium?',
        a: 'Go to the Membership page, choose a plan, and complete payment through Stripe Checkout. After successful payment, your Premium access should activate.',
      },
      {
        q: 'What payment methods are accepted?',
        a: 'Payments are processed through Stripe. Available payment methods may depend on Stripe checkout options at the time of payment.',
      },
      {
        q: 'Does JSN store my card details?',
        a: 'No. JSN does not store full card details. Payments are processed through Stripe.',
      },
      {
        q: 'I paid but still see free features. What should I do?',
        a: 'First refresh the page. If needed, log out and log back in. Premium access should update after successful payment. If it still does not update, check again later as payment confirmation may take a short time.',
      },
      {
        q: 'Are payments refundable?',
        a: "Payment and refund policies may vary by plan and will be shown during checkout or in JSN's posted terms. Do not assume a payment is refundable unless a posted JSN policy clearly says so.",
      },
      {
        q: 'Is Premium activated immediately after payment?',
        a: 'Premium should activate after a successful Stripe payment and redirect back to JSN. If access does not appear immediately, refresh or log out and back in.',
      },
    ],
  },
  {
    title: 'Messages, Matches, and Profile Views',
    items: [
      {
        q: "Why can't I send messages?",
        a: 'Messaging is available to Premium users. If you are using a free account, you will need to upgrade to Premium to read or send messages.',
      },
      {
        q: "Why can't I open a full profile?",
        a: 'Full profile view is a Premium feature. Free users can browse basic profile cards and use Like or Pass.',
      },
      {
        q: "Why can't I see who liked me?",
        a: 'Liked-me and match visibility may require Premium access. Upgrade to Premium to unlock full match features.',
      },
      {
        q: 'Why are some profiles no longer visible?',
        a: "A profile may disappear if you passed on it, blocked it, if the user's account was suspended/deactivated, or if the profile is no longer available.",
      },
      {
        q: "Why can't I see my own profile in Discover?",
        a: 'Your own profile does not appear in your Discover feed. Other eligible members may see your profile depending on your approval status, filters, and account visibility.',
      },
    ],
  },
  {
    title: 'Safety, Blocking, and Reporting',
    items: [
      {
        q: 'How do I block someone?',
        a: 'Blocking may be available through Premium member interaction tools. When you block someone, JSN should prevent that profile from appearing in your Discover or interaction areas where applicable.',
      },
      {
        q: 'What happens when I block someone?',
        a: 'Blocking helps prevent unwanted interaction. The blocked profile should no longer appear to you in normal browsing or interaction areas.',
      },
      {
        q: 'Can I unblock someone?',
        a: 'Unblocking may require admin review or future account tools. If unblock tools are available, they should be shown inside the app.',
      },
      {
        q: 'How do I report a profile?',
        a: 'Reporting is used for safety concerns and may be available through Premium member interaction tools. Reports may be reviewed by an admin.',
      },
      {
        q: 'Can a reported user see who reported them?',
        a: 'Reports should be handled privately. JSN should not publicly show who submitted a report.',
      },
      {
        q: 'What behavior is not allowed on JSN?',
        a: 'JSN does not allow harassment, threats, scams, impersonation, fake profiles, inappropriate images, requests for money, abusive behavior, or misuse of the platform. Accounts may be reviewed, suspended, deactivated, or deleted if they violate JSN safety standards.',
      },
      {
        q: 'What should I do if someone asks me for money?',
        a: 'Be cautious. Do not send money to someone you met through JSN or any online platform. If reporting tools are available, report the profile immediately.',
      },
    ],
  },
  {
    title: 'Profile, Visibility, and Account Settings',
    items: [
      {
        q: 'Can I edit my profile?',
        a: 'Yes. You can use Account Settings to update supported profile details. Some changes, such as replacing your photo, may require review again.',
      },
      {
        q: 'Can I change my photo?',
        a: 'Yes. If you upload a new photo, it may need to be reviewed before it is approved.',
      },
      {
        q: 'Why does my profile say pending review?',
        a: 'Your photo or profile may still be awaiting admin approval. You will not have full member access until the required review is complete.',
      },
      {
        q: "Why don't I see many profiles yet?",
        a: 'JSN may have fewer profiles during early launch. The number of visible profiles can also depend on filters, account approval status, and member availability.',
      },
      {
        q: 'Can I change my phone number?',
        a: 'Phone number changes may require re-verification. If phone editing is available, the new number must be a valid U.S. +1 number.',
      },
      {
        q: 'Can I delete my account?',
        a: 'Yes. If available, go to Account Settings and use the Delete Account option in the Danger Zone section. You may be asked to confirm before deletion is completed.',
      },
    ],
  },
  {
    title: 'Privacy and Account Deletion',
    items: [
      {
        q: 'Who can see my profile?',
        a: 'Approved members may be able to see your basic profile in Discover, depending on filters, account status, and app rules. Pending, suspended, or deactivated accounts should not appear in normal member discovery.',
      },
      {
        q: 'Can other members see my email address?',
        a: 'No. Your email address is used for login, verification, and account security. It should not be shown publicly to other members.',
      },
      {
        q: 'Can other members see my phone number?',
        a: 'No. Your phone number is used for verification and account security. It should not be shown publicly to other members.',
      },
      {
        q: 'What happens when I delete my account?',
        a: 'Your profile should be removed from member discovery and your account access should end. Some records may remain where needed for security, payments, moderation, or legal compliance.',
      },
      {
        q: 'Can I reactivate a deleted account?',
        a: 'Deleted accounts may not be recoverable. If you delete your account, you may need to create a new one later, subject to JSN rules.',
      },
    ],
  },
  {
    title: 'Troubleshooting',
    items: [
      {
        q: 'I am stuck between login and registration. What should I do?',
        a: 'If the app says your account exists but also says it cannot find your profile, your setup may be incomplete. Try signing in again to continue setup. JSN includes account repair tools to help resolve incomplete account records.',
      },
      {
        q: 'The app says my account is suspended. What does that mean?',
        a: 'A suspended account cannot access member features. Accounts may be suspended for safety, policy, verification, or admin review reasons.',
      },
      {
        q: 'I received a password reset email but the link does not work. What should I do?',
        a: 'Password reset links may expire or become invalid after use. Request a new password reset email and use the newest link.',
      },
      {
        q: 'Why does my verification code not work?',
        a: 'Use the most recent code sent to you. If you requested a new code, older codes may no longer work. Codes may also expire after a short time.',
      },
      {
        q: 'Why did my payment succeed but my app still looks the same?',
        a: 'Try refreshing the page or logging out and back in. If Premium access is active, Premium features should unlock.',
      },
      {
        q: "Why can't I access Discover?",
        a: 'You may still need to verify your email, verify your phone, upload a photo, wait for photo approval, or resolve an account status issue.',
      },
    ],
  },
];

function AccordionItem({ item, isOpen, onToggle }: { item: FAQItem; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border-b border-border/50 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-4 py-4 text-left group"
        aria-expanded={isOpen}
      >
        <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors leading-snug">
          {item.q}
        </span>
        <span className="flex-shrink-0 mt-0.5 text-muted-foreground group-hover:text-primary transition-colors">
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>
      {isOpen && (
        <div className="pb-4 pr-8">
          <p className="text-sm text-muted-foreground leading-relaxed">{item.a}</p>
        </div>
      )}
    </div>
  );
}

function AccordionSection({ section, sectionIndex }: { section: FAQSection; sectionIndex: number }) {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  const [sectionOpen, setSectionOpen] = useState(sectionIndex === 0);

  const toggleItem = (idx: number) => {
    setOpenItems(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <button
        onClick={() => setSectionOpen(!sectionOpen)}
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-muted/10 transition-colors"
        aria-expanded={sectionOpen}
      >
        <div className="flex items-center gap-3">
          <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">
            {sectionIndex + 1}
          </span>
          <span className="text-base font-bold text-foreground">{section.title}</span>
          <span className="text-xs text-muted-foreground hidden sm:block">({section.items.length} questions)</span>
        </div>
        <span className="text-muted-foreground flex-shrink-0">
          {sectionOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>

      {sectionOpen && (
        <div className="px-6 pb-2 border-t border-border/50">
          {section.items.map((item, idx) => (
            <AccordionItem
              key={`faq-${sectionIndex}-${idx}`}
              item={item}
              isOpen={openItems.has(idx)}
              onToggle={() => toggleItem(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function HelpPage() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      setIsAuthenticated(!!user);
    };
    checkAuth();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Auth-aware header */}
      {isAuthenticated === true ? (
        <AppNav activePage="help" />
      ) : isAuthenticated === false ? (
        <header className="bg-card/95 backdrop-blur-md border-b border-border sticky top-0 z-40">
          <div className="max-w-screen-lg mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
            <Link href="/" className="flex items-center gap-2 no-underline">
              <AppLogo size={36} />
              <div>
                <div className="font-extrabold text-base text-foreground leading-tight">Jamuhuri Singles Network</div>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <Link
                href="/sign-up-login-screen"
                className="text-sm text-muted-foreground hover:text-primary transition-colors font-medium"
              >
                Sign In
              </Link>
              <Link
                href="/sign-up-login-screen"
                className="btn-primary px-4 py-1.5 rounded-lg text-xs font-semibold"
              >
                Join JSN
              </Link>
            </div>
          </div>
        </header>
      ) : null /* loading — render nothing until auth state is known */}

      <main className={`max-w-screen-lg mx-auto px-4 sm:px-6 py-10 sm:py-14${isAuthenticated === true ? ' pt-24' : ''}`}>
        {/* Page Title */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 mb-4">
            <HelpCircle size={22} className="text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-foreground mb-3">Help &amp; FAQ</h1>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            Answers to common questions about Jamuhuri Singles Network. Select a section below to expand it.
          </p>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-3">
          {faqSections.map((section, idx) => (
            <AccordionSection key={`section-${idx}`} section={section} sectionIndex={idx} />
          ))}
        </div>

        {/* Footer Note */}
        <div className="mt-10 bg-card border border-border rounded-2xl p-6 text-center">
          <p className="text-sm text-muted-foreground leading-relaxed">
            JSN is continuing to improve its help resources. If your issue is not covered here, please check back soon. Additional support options may be added as the community grows.
          </p>
        </div>

        {/* Back links */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-sm">
          <Link href={isAuthenticated ? '/home' : '/'} className="text-muted-foreground hover:text-primary transition-colors">
            ← Back to Home
          </Link>
          {!isAuthenticated && (
            <>
              <span className="text-border">|</span>
              <Link href="/sign-up-login-screen" className="text-muted-foreground hover:text-primary transition-colors">
                Sign In / Create Account
              </Link>
            </>
          )}
          <span className="text-border">|</span>
          <Link href="/safety" className="text-muted-foreground hover:text-primary transition-colors">
            Safety &amp; Guidelines
          </Link>
        </div>
      </main>

      <SafetyAndFooter />
    </div>
  );
}
